// RailsFormElementNamePath
// Rails fields_forが生成するname属性のパターン生成・解析等の機能
// 
// ---------- 作成例1 配列から
// const namePath = RailsFormElementNamePath.make(['estimation', 'estimation_details_attributes', 0, 'estimation_detail_items_attributes', 0, 'content'])
// 
// ---------- 作成例2 文字列から
// const namePath2 = RailsFormElementNamePath.make('estimation[estimation_details_attributes][0][estimation_detail_items_attributes][0][content]')
// 
// ---------- インデックスの書き換え例1 （末尾のインデックス）
// namePath.make({index: 100}).toString() // => 'estimation[estimation_details_attributes][0][estimation_detail_items_attributes][100][content]'
//
// ---------- インデックスの書き換え例2 offset でパスの左からどの位置(次元)のオブジェクトのインデックスを書き換えるか指定する 負の値でパスの右から指定
// namePath.make({index: 200, offset: 0}).toString() // => offset 0 は 'estimation' の所属インデックスを指す 所属インデックスを持たないため変化なし
// namePath.make({index: 200, offset: 1}).toString() // => offset 1 は 'estimation_details_attributes' の所属インデックスを指す 所属インデックスを持たないため変化なし
// namePath.make({index: 200, offset: 2}).toString() // => 'estimation[estimation_details_attributes][200][estimation_detail_items_attributes][0][content]'
// namePath.make({index: 200, offset: -1}).toString() // => 'estimation[estimation_details_attributes][0][estimation_detail_items_attributes][200][content]'
// namePath.make({index: 200, offset: -2}).toString() // => 'estimation[estimation_details_attributes][200][estimation_detail_items_attributes][0][content]'
//
// ---------- フィールド名の書き換え例 field を指定して書き換え（末尾）、parent で親要素の名前を書き換え、offset も指定可
// namePath.make({field: 'remark'}).toString() // => 'estimation[estimation_details_attributes][0][estimation_detail_items_attributes][0][remark]'
// namePath.make({parent: 'bbb_attributes'}).toString() // => 'estimation[estimation_details_attributes][0][bbb_attributes][0][content]'
// namePath.make({field: 'remark', offset: -2}).toString() // => 'estimation[estimation_details_attributes][0][remark][0][content]'
// namePath.make({field: 'remark', offset: -4}).toString() // => 'remark[estimation_details_attributes][0][estimation_detail_items_attributes][0][content]'
// namePath.make({field: 'remark', offset: 0}).toString() // => 'remark[estimation_details_attributes][0][estimation_detail_items_attributes][0][content]'
// namePath.make({field: 'remark', offset: 1}).toString() // => 'estimation[remark][0][estimation_detail_items_attributes][0][content]'
// namePath.make({parent: 'remark', offset: 1}).toString() // => 'remark[estimation_details_attributes][0][estimation_detail_items_attributes][0][content]'
// 

/** パスの種類 
 ** 対応例： 'school[nearby_school_relations_attributes][2][school_id]' 👉 root[parent][index][current] 
*/
type PathType = 'root' | 'parent' | 'index' | 'current';

/** make関数でインスタンス生成する際に利用できるソースの型 */
type MakeSourceType = RailsFormElementNamePath | Readonly<RailsFormElementNamePathPrimitives> | string;

/** input要素のname属性を分解した際の型 */
export type RailsFormElementNamePathPrimitive = string | number;

/** input要素のname属性を分解した際の型(配列) */
export type RailsFormElementNamePathPrimitives = RailsFormElementNamePathPrimitive[];

/** InputElementNamePath.makeメソッドオプション引数 */
export interface RailsFormElementNamePathMakeOptions {
  // 参照対象のオブジェクトの階層
  offset?: number | undefined;

  /** フィールド(カラム)名 */
  field?: string | undefined;

  /** 親フィールド名 */
  parent?: string | undefined;

  /** 何行目のデータか */
  index?: number | undefined;
}

/** input要素のname属性を管理するクラス
 ** railsでネストしたfields_forで生成されるinput要素のname属性値を表現する
 ** 外側からアクセスできるメソッドは基本的にimmutable、変更時はmakeメソッドで複製する
*/
export class RailsFormElementNamePath {

  /** 分解されたパス情報
   ** 例 ['school', 'nearby_school_relation_attributes', 2, 'school_id'] 
   */
  private _splittedPath: RailsFormElementNamePathPrimitive[];

  /** 参照・編集対象のfields_forの階層数 */
  private _defaultoffset: number | undefined;

  /** 分解されたname属性を取得
   ** 例 'school[nearby_school_relations_attributes][2][school_id]' => ['school', 'nearby_school_relation', 2, 'school_id'] 
   * 
  */
  public get splittedPath(): RailsFormElementNamePathPrimitives {
    return Array.from(this._splittedPath);
  }

  /** name属性を取得
   ** 例 ['school', 'nearby_school_relation', 2, 'school_id'] => 'school[nearby_school_relations][2][school_id]' 
   * 
  */
  public get fullName(): string {
    return RailsFormElementNamePath.toNameFormSplittedPath(this._splittedPath);
  }

  /** フィールド名を取得 
   ** 例： ['school', 'nearby_school_relation', 10, 'school_id'] なら 'school_id' を返却 
   * 
  */
  public get fieldName(): string | undefined {
    return this.getFieldName();
  }

  /** 親フィールド名を取得
   ** 例： ['school', 'nearby_school_relation', 10, 'school_id'] なら 'nearby_school_relation' を返却 
  */
  public get parentName(): string | undefined {
    return this.getParentName();
  }

  /** 最上層フィールド名を取得する
   * 例： ['school', 'nearby_school_relation', 10, 'school_id'] なら 'school' を返却 
  */
  public get rootName(): string | undefined {
    return this.getRootName();
  }

  /** コレクションに所属す場合、自身は何番目かを取得する
   ** 例： ['school', 'nearby_school_relation', 10, 'school_id'] なら 10 を返却 
  */
  public get index(): number | undefined {
    return this.getIndex();
  }

  /** オブジェクト最大階層数 
   ** 例 'estimation[estimation_details_attributes][10][estimation_detail_items_attributes][20][content]' => 3 
   ** 例 'estimation[estimation_cost_attributes][text_cost_details_attributes][100][name]' => 3 
   ** 例 'estimation[estimation_cost_attributes][id]' => 2
   ** 例 'estimation[content]' => 1
   ** 例 'estimation_content' => 0
  */
  public get maxNestCount(): number {
    return this.getObjectIndexList().length;
  }

  /** 現在参照対象のオブジェクトの階層インデックス */
  public get currentOffset(): number {
    /** 最大値 */
    const maxNestCount = this.maxNestCount;

    // デフォルト値が設定ない場合は最大値を返す
    if (this._defaultoffset == undefined || maxNestCount == 0) {
      return maxNestCount;
    }

    // offsetが負の値の場合は後ろから
    const currentoffset = this._defaultoffset >= 0 ? this._defaultoffset : (maxNestCount + this._defaultoffset + 1);

    // 
    return currentoffset;
  }

  /** name属性を取得
   ** 例 ['school', 'nearby_school_relation_attributes', 2, 'school_id'] => 'school[nearby_school_relations_attributes][2][school_id]'  
  */
  public static toNameFormSplittedPath(splittedPath: Readonly<RailsFormElementNamePathPrimitives>): string {
    let name = '';

    name += splittedPath[0];

    for (let i = 1; i < splittedPath.length; ++i) {
      name += `[${splittedPath[i]}]`;
    }

    return name;
  }

  /** 分解されたname属性を取得
   * 例 'school[nearby_school_relations_attributes][2][school_id]' => ['school', 'nearby_school_relation_attributes', 2, 'school_id']
  */
  public static toSplittedPathFromName(name: string): RailsFormElementNamePathPrimitives {

    /** パス分解時に一時的に置換する文字列 */
    const TMP_SPLIT_TOKEN = "\n";

    /** 返却用 */
    const splittedPath: RailsFormElementNamePathPrimitives = [];

    /** name属性に指定されたパスを分解 */
    const splittedPathTmp = name.replace(/(\]\[)|\[|\]/g, TMP_SPLIT_TOKEN).split(TMP_SPLIT_TOKEN).filter((s) => !!s.trim());

    // パス解析 数値に変換できる文字列は数値に変換
    for (const name of splittedPathTmp) {
      if (isNaN(Number(name))) {
        splittedPath.push(name);
      }
      else {
        splittedPath.push(Number(name));
      }
    }

    return splittedPath;
  }

  /** インスタンスから複製(オーバーロード) */
  public static make(source: RailsFormElementNamePath, options?: RailsFormElementNamePathMakeOptions): RailsFormElementNamePath;

  /** name属性の文字列からインスタンスを生成 */
  public static make(name: string, options?: RailsFormElementNamePathMakeOptions): RailsFormElementNamePath;

  /** 分解済みname属性の配列からインスタンス生成 */
  public static make(splittedPath: Readonly<RailsFormElementNamePathPrimitives>, options?: RailsFormElementNamePathMakeOptions): RailsFormElementNamePath;

  /** インスタンス生成(オーバーロード) */
  public static make(source: MakeSourceType, options?: RailsFormElementNamePathMakeOptions): RailsFormElementNamePath;

  /** インスタンス生成(オーバーロード) */
  public static make(source: MakeSourceType, options?: RailsFormElementNamePathMakeOptions): RailsFormElementNamePath {
    // 新しいインスタンス
    let newInstance: RailsFormElementNamePath;

    // インスタンスから複製(オーバーロード)
    if (source instanceof RailsFormElementNamePath) {
      return source.make(options);
    }

    // name属性の文字列からインスタンスを生成(オーバーロード)
    else if (typeof source == 'string') {
      newInstance = new RailsFormElementNamePath(RailsFormElementNamePath.toSplittedPathFromName(source), options?.offset);
    }

    // 分解済みname属性の配列からインスタンス生成(オーバーロード)
    else {
      newInstance = new RailsFormElementNamePath(source, options?.offset);
    }

    // インデックスやカラム名を設定
    newInstance.set(options);

    // 
    return newInstance;
  }

  /** 指定されたインデックスやフィールド名で置き換えてname属性を取得 */
  public make(options?: RailsFormElementNamePathMakeOptions) {
    const cloned = new RailsFormElementNamePath(this._splittedPath, options?.offset ?? this._defaultoffset);
    cloned.set(options);
    return cloned;
  }

  /** name属性を取得
   ** 例 ['school', 'nearby_school_relation', 2, 'school_id'] => 'school[nearby_school_relations_attributes][2][school_id]' 
   * 
  */
  public toString(): string {
    return this.fullName;
  }

  /** インデックス 取得
   *  @param offset ネスト数 負の数にすると後ろから参照 (規定値:-1)
  */
  public getIndex(options?: { offset?: number }): number | undefined {

    /** 参照位置 */
    const targetPos = this.getTargetIndex({ offset: options?.offset, type: 'index' });

    // 存在しない階層
    if (targetPos == null) {
      return undefined;
    }

    /* 例： ['school', 'nearby_school_relation', 10, 'school_id'] なら 10 を返却 */
    return this._splittedPath[targetPos] as number | undefined;
  }

  /** フィールド名 取得
   *  @param offset ネスト数 負の数にすると後ろから参照 (規定値:-1)
  */
  public getFieldName(options?: { offset?: number }): string | undefined {
    /** 参照位置 */
    const targetPos = this.getTargetIndex({ offset: options?.offset, type: 'current' });

    // 存在しない階層
    if (targetPos == null) {
      return undefined;
    }

    /* 例： path: ['school', 'nearby_school_relation', 10, 'school_id'], nest: -1 なら 'school_id' を返却 */
    return this._splittedPath[targetPos] as string;
  }

  /** 親名 取得
   *  @param offset ネスト数 負の数にすると後ろから参照 (規定値:-1)
  */
  public getParentName(options?: { offset?: number }): string | undefined {
    /** 参照位置 */
    const targetPos = this.getTargetIndex({ offset: options?.offset, type: 'parent' });

    // 存在しない階層
    if (targetPos == null) {
      return undefined;
    }

    /* 例： path: ['school', 'nearby_school_relation', 10, 'school_id'], nest: -1 なら 'nearby_school_relation' を返却 */
    return this._splittedPath[targetPos] as string;
  }

  /** ルート名 取得 */
  public getRootName(): string | undefined {
    return this._splittedPath[0] as string;
  }

  /** インデックス 設定 
   *  @param offset ネスト数 負の数にすると後ろから参照 (規定値:-1)
  */
  private setIndex(value: number, options?: { offset?: number | undefined }): boolean {
    /** 参照位置 */
    const targetPos = this.getTargetIndex({ offset: options?.offset, type: 'index' });

    // 存在しない階層
    if (targetPos == null) {
      return false;
    }

    /* 例： ['school', 'nearby_school_relation', 10, 'school_id'] で valueが 11なら
     * ['school', 'nearby_school_relation', 11, 'school_id'] になる
     */
    this._splittedPath[targetPos] = value;
    return true;
  }

  /** フィールド名 設定 
   *  @param offset ネスト数 負の数にすると後ろから参照 (規定値:-1)
  */
  private setFieldName(value: string, options?: { offset?: number | undefined }): boolean {
    /** 参照位置 */
    const targetPos = this.getTargetIndex({ offset: options?.offset, type: 'current' });

    // 存在しない階層
    if (targetPos == null) {
      return false;
    }

    /* 例： ['school', 'nearby_school_relation', 10, 'school_id'] で valueが 'display_order' なら
     * ['school', 'nearby_school_relation', 11, 'display_order'] になる
     */
    this._splittedPath[targetPos] = value;
    return true;
  }

  /** 親名 設定 
   *  @param offset ネスト数 負の数にすると後ろから参照 (規定値:-1)
  */
  private setParentName(value: string, options?: { offset?: number | undefined }): boolean {
    /** 参照位置 */
    const targetPos = this.getTargetIndex({ offset: options?.offset, type: 'parent' });

    // 存在しない階層
    if (targetPos == null) {
      return false;
    }

    /* 例： ['school', 'nearby_school_relation', 10, 'school_id'] で valueが 'area_relation' なら
     * ['school', 'area_relation', 11, 'school_id'] になる
     */
    this._splittedPath[targetPos] = value;
    return true;
  }

  /** インデックス、フィールド名、親フィールド名の設定 */
  private set(options?: RailsFormElementNamePathMakeOptions) {
    if (options?.index != null) {
      this.setIndex(options.index, { offset: options.offset });
    }
    if (options?.field != null) {
      this.setFieldName(options.field, { offset: options.offset });
    }
    if (options?.parent != null) {
      this.setParentName(options.parent, { offset: options.offset });
    }
  }

  /** 分解されたname属性の指定された要素位置の取得 */
  private getTargetIndex({ type, offset }: { type: PathType, offset: number | undefined }): number | undefined {

    // 対象のfields_for階層
    offset ??= this._defaultoffset;

    // デフォルト設定
    if (offset == null) {
      offset = -1;
    }

    /** パスからオブジェクト名の位置を取得
     ** 例： ['school', 'nearby_school_relations_attributes', 10, 'schools_attributes', 15, 'name'] なら [0, 1, 3] を取得する 
     * ※ school と nearby_school_relations_attributes と schools_attributes の位置
     * 
    */
    const objectIndexList = this.getObjectIndexList();

    /** 基準となるインデックス */
    const getBaseObjectIndexListPos = ({ positiveDiff, negativeDiff }: { positiveDiff?: number, negativeDiff?: number }) =>
      (offset >= 0) ? (offset + (positiveDiff ?? 0)) : (objectIndexList.length + offset + (negativeDiff ?? 0));

    // どの位置の(index)を取得するか
    // 参考： 
    // 元のパス👉'school[nearby_school_relations_attributes][10][schools_attributes][15][name]' 
    // 分解後👉 ['school', 'nearby_school_relations_attributes', 10, 'schools_attributes', 15, 'name']
    // nest👉-1 
    // タイプごとの取得位置👉　root[][][parent][index][current] 
    // 実際に取得されるインデックス👉　'root': 0, 'parent': 3, 'index': 4, 'current': 5
    let pos: number | undefined;
    switch (type) {
      // 大体の場合がモデル名に相当
      case 'root': {
        pos = 0;
      } break;

      // 大体の場合がリレーション名に相当
      case 'parent': {
        /** 参照箇所のネガティブ方向からの取得は 1 ずれる */
        const baseObjectIndexListPos = getBaseObjectIndexListPos({ negativeDiff: +1 });

        // offset が 0 の場合は最上層なので親は存在しない
        if (baseObjectIndexListPos == 0) {
          pos = undefined;
        }

        // そもそも１階層しかない場合も親は自身であるため存在しない
        else if (this._splittedPath.length == 1) {
          pos = undefined;
        }

        // 
        else {
          /** オブジェクトの位置 一つ前が親  */
          const objectIndex = objectIndexList[baseObjectIndexListPos - 1];

          // 想定外
          if (objectIndex == null) {
            pos = undefined;
          }

          // 
          else {
            pos = objectIndex;
          }
        }
      } break;

      // 何行目か（数値部分）に相当
      case 'index': {
        /** 参照箇所の正方向からの取得は 1 ずれる */
        const baseObjectIndexListPos = getBaseObjectIndexListPos({ positiveDiff: -1 });

        /** オブジェクトの位置  */
        const objectIndex = objectIndexList[baseObjectIndexListPos];

        // 想定外
        if (objectIndex == null) {
          pos = undefined;
        }
        // 
        else {

          // has_manyであればオブジェクトのすぐ隣はインデックス(number型)になる
          if (typeof this._splittedPath[objectIndex + 1] == 'number') {
            pos = objectIndex + 1;
          }

          // has_oneはインデックスがない
          else {
            pos = undefined;

          }

        }
      } break;

      // カラム名に相当
      case 'current': {
        /** 参照箇所のネガティブ方向からの取得は 1 ずれる */
        const baseObjectIndexListPos = getBaseObjectIndexListPos({ negativeDiff: +1 });

        // rootを指している場合はrootの位置(0)をそのまま利用
        if (baseObjectIndexListPos == 0) {
          pos = 0;

        }
        else {
          /** オブジェクトの位置  */
          const objectIndex = objectIndexList[baseObjectIndexListPos - 1];

          // 想定外
          if (objectIndex == null) {
            pos = undefined;
          }
          // 
          else {

            // has_manyの場合はリレーション名の隣にインデックスがあるのさらに1つ飛ばし
            if (typeof this._splittedPath[objectIndex + 1] == 'number') {
              pos = objectIndex + 2;
            }

            // has_oneの場合はリレーション名のすぐ隣がカラム名
            else if (typeof this._splittedPath[objectIndex + 1] == 'string') {
              pos = objectIndex + 1;
            }

            // オブジェクトネストがない
            else if (this._splittedPath.length == 1) {
              pos = 0;
            }

            // 想定外
            else {
              pos = undefined;
            }
          }
        }
      } break;
    }

    // 範囲外チェック
    if (pos != undefined && pos >= this._splittedPath.length) {
      pos = undefined;
    }

    return pos;
  }

  /** 子要素やカラムを持つオブジェクトの位置を取得(インデックスの位置と最後のフィールドの位置は含まない)
   ** 例 'estimation[estimation_details_attributes][10][estimation_detail_items_attributes][20][content]' => [0, 1, 3]
   ** 例 'estimation[estimation_cost_attributes][text_cost_details_attributes][100][name]' => [0, 1, 2]
   ** 例 'estimation[estimation_cost_attributes][id]' => [0, 1]
   ** 例 'estimation[content]' => [0]
   ** 例 'estimation_content' => []
  */
  private getObjectIndexList(): number[] {
    /** オブジェクトのネストの位置を記録する用 */
    const relationPosList = [];

    // パスの最後は単純なフィールド名とするため省略するのでlength-1
    for (let index = 0; index < this._splittedPath.length - 1; ++index) {

      const nextPath = this._splittedPath[index + 1];

      // has_manyでインデックス指定(number型)がある
      if (typeof nextPath == 'number') {
        relationPosList.push(index);
        // インデックスの位置を飛ばす
        index += 1;
      }

      // has_oneでインデックス指定がない
      else if (typeof nextPath == 'string') {
        relationPosList.push(index);
      }

      // パス終端（もしくは不正な型）
      else {
        // なにもしない
      }
    }

    // 
    return relationPosList;
  }

  /** コンストラクタ make関数から間接的に作成することを想定 */
  private constructor(splittedPath: Readonly<RailsFormElementNamePathPrimitives>, defaultNest: number | undefined) {
    if (splittedPath.length == 0) {
      throw new Error("Invalid Arguments");
    }
    this._splittedPath = Array.from(splittedPath);
    this._defaultoffset = defaultNest;
  }
}
